import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { useForm } from '@inertiajs/react'
import { FormEvent } from 'react'
import { Building2 } from 'lucide-react'

interface CommunityData {
  id?: number
  slug?: string
  name: string
  description: string
  active: boolean
  specialties: string[]
}

interface CommunityFormProps {
  community?: CommunityData
  specialties: string[]
  isEdit?: boolean
}

export default function CommunityForm({ community, specialties: availableSpecialties, isEdit = false }: CommunityFormProps) {
  const { data, setData, post, put, processing, errors } = useForm({
    name: community?.name || '',
    description: community?.description || '',
    active: community?.active ?? true,
    specialties: community?.specialties?.join(', ') || ''
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isEdit && community?.slug) {
      put(`/admin/communities/${community.slug}`)
    } else {
      post('/admin/communities')
    }
  }

  const addSpecialty = (specialty: string) => {
    const current = data.specialties ? data.specialties.split(',').map(s => s.trim()).filter(Boolean) : []
    if (!current.includes(specialty)) {
      setData('specialties', [...current, specialty].join(', '))
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card className="p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200">
          <div className="w-10 h-10 bg-tessan-cream rounded-lg flex items-center justify-center">
            <Building2 size={20} className="text-tessan-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-text-dark">
              {isEdit ? 'Modifier la communauté' : 'Nouvelle communauté'}
            </h2>
            <p className="text-sm text-text-gray">
              {isEdit ? 'Modifiez les informations de la communauté' : 'Créez une nouvelle communauté'}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-text-dark mb-1">
              Nom *
            </label>
            <Input
              id="name"
              type="text"
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
              placeholder="ex: Médecins"
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
            <p className="mt-1 text-xs text-text-gray">
              Le slug sera généré automatiquement à partir du nom.
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-text-dark mb-1">
              Description
            </label>
            <Textarea
              id="description"
              value={data.description}
              onChange={(e) => setData('description', e.target.value)}
              placeholder="Description de la communauté..."
              rows={3}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Specialties */}
          <div>
            <label htmlFor="specialties" className="block text-sm font-medium text-text-dark mb-1">
              Spécialités
            </label>
            <Input
              id="specialties"
              type="text"
              value={data.specialties}
              onChange={(e) => setData('specialties', e.target.value)}
              placeholder="Généraliste, Cardiologue, Dermatologue..."
            />
            <p className="mt-1 text-xs text-text-gray">
              Séparez les spécialités par des virgules.
            </p>

            {/* Quick add buttons */}
            <div className="mt-2">
              <p className="text-xs text-text-gray mb-2">Ajouter rapidement :</p>
              <div className="flex flex-wrap gap-1">
                {availableSpecialties.slice(0, 6).map((specialty) => (
                  <button
                    key={specialty}
                    type="button"
                    onClick={() => addSpecialty(specialty)}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                  >
                    + {specialty}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Active */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.active}
                onChange={(e) => setData('active', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-tessan-primary focus:ring-tessan-primary"
              />
              <div>
                <span className="font-medium text-text-dark">Communauté active</span>
                <p className="text-sm text-text-gray">
                  Les communautés inactives ne sont pas visibles pour les utilisateurs.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end gap-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => history.back()}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Créer la communauté'}
          </Button>
        </div>
      </Card>
    </form>
  )
}
